import { useMutation, useQuery, useQueryClient } from "react-query";
import { CreateMatch, Match } from "../models/Match";
import { useBackendMutationFunction } from "./common/useBackendMutationFunction";
import { useBackendQueryFunction } from "./common/useBackendQueryFunction";
import { UrlQuery } from "./common/useBackendUrl";
import { LeagueTeam } from "@/models/LeagueTeam";
import { LeaguePlayer } from "@/models/LeaguePlayer";
import { LeagueDouble } from "@/models/Player";
import { MatchState, Side } from "@prisma/client";
import { useState } from "react";

export const GetMatchesKey = "GetMatches";
export const GetVideoWidgetKey = "GetVideoWidget";

interface UseGetMatchProps extends UrlQuery {
  id: string;
}

export function useGetMatch(props: UseGetMatchProps) {
  const queryFn = useBackendQueryFunction<Match>("/matches/:id", props);

  return useQuery<Match>([GetMatchesKey, props], queryFn);
}

interface UseGetMatchesProps extends UrlQuery {
  seasonId: string;
}

export function useGetMatches(props: UseGetMatchesProps) {
  const queryFn = useBackendQueryFunction<Match[]>("/matches", props);

  return useQuery<Match[]>([GetMatchesKey, props], queryFn);
}

interface UseGetMatchesOfLeagueProps extends UrlQuery {
  seasonId: string;
}
export function useGetMatchesOfBundesliga(props: UseGetMatchesOfLeagueProps) {
  const queryFn = useBackendQueryFunction<Match[]>(
    "/season/:seasonId/bundesliga/matches",
    props
  );

  return useQuery<Match[]>([GetMatchesKey, props], queryFn);
}
export function useGetMatchesOfGameday(props: UseGetMatchProps) {
  const queryFn = useBackendQueryFunction<Match[]>(
    "/gameday/:id/matches",
    props
  );

  return useQuery<Match[]>([GetMatchesKey, props], queryFn);
}

export function useGetMatchesOfPokal(props: UseGetMatchesOfLeagueProps) {
  const queryFn = useBackendQueryFunction<Match[]>(
    "/season/:seasonId/pokal/matches",
    props
  );

  return useQuery<Match[]>([GetMatchesKey, props], queryFn);
}
interface UseDeleteMatchProps extends UrlQuery {
  matchId: string;
}
export function useDeleteMatch(props: UseDeleteMatchProps) {
  const mutationFn = useBackendMutationFunction<Match, void>(
    "/matches/:matchId",
    "DELETE",
    props
  );
  const queryClient = useQueryClient();
  return useMutation(mutationFn, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(GetMatchesKey);
    },
  });
}

export function usePostMatch() {
  const mutationFn = useBackendMutationFunction<Match, CreateMatch>(
    "/matches",
    "POST"
  );
  const queryClient = useQueryClient();
  return useMutation(mutationFn, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(GetMatchesKey);
    },
  });
}

export function usePatchMatch() {
  const mutationFn = useBackendMutationFunction<Match, Match>(
    "/matches",
    "PATCH"
  );
  const queryClient = useQueryClient();
  return useMutation(mutationFn, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(GetMatchesKey);
    },
  });
}

export interface VideoWidgetProps {
  homeTeam: LeagueTeam;
  awayTeam: LeagueTeam;
  currentSet: number;
  homeGames: number;
  awayGames: number;
  set1AwayScore: number;
  set1HomeScore: number;
  set2AwayScore: number;
  set2HomeScore: number;
  set3AwayScore: number;
  set3HomeScore: number;
  set4AwayScore: number;
  set4HomeScore: number;
  set5AwayScore: number;
  set5HomeScore: number;
  homeSets: number;
  awaySets: number;
  gameday: { name: string };
  homePlayer?: LeaguePlayer;
  awayPlayer?: LeaguePlayer;
  homeDouble?: LeagueDouble;
  awayDouble?: LeagueDouble;
  homePlayerOneId: string;
  homePlayerTwoId: string;
  homePlayerThreeId: string;
  guestPlayerOneId: string;
  guestPlayerTwoId: string;
  guestPlayerThreeId: string;
  homeSubstitutePlayerOneId: string;
  homeSubstitutePlayerTwoId: string;
  homeSubstitutePlayerThreeId: string;
  guestSubstitutePlayerOneId: string;
  guestSubstitutePlayerTwoId: string;
  guestSubstitutePlayerThreeId: string;
  matchState: MatchState;
  currentServer: Side;
  index: number;
}

interface UseGetVideoWidgetProps extends UrlQuery {
  matchId: string;
}
export function useGetVideoWidget(props: UseGetVideoWidgetProps) {
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const queryFn = useBackendQueryFunction<VideoWidgetProps>(
    "/videoWidget/:matchId",
    props
  );

  return useQuery<VideoWidgetProps>([GetVideoWidgetKey, props], queryFn, {
    refetchInterval: shouldRefetch ? 1000 : false,
    onSuccess: (match) => {
      if (match.matchState === "Active" && !shouldRefetch) {
        setShouldRefetch(true);
      } else if (match.matchState !== "Active" && shouldRefetch) {
        setShouldRefetch(false);
      }
    },
  });
}
